import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import numeral from 'numeral';
import { toast } from 'react-hot-toast';
import Bugsnag from '@/bugsnag';
import config from '@/config';
import enMessages from '@/locales/en.json';
import statuses from '@/statuses';
import { getPersistentStore } from '@/storage/persistent-store';
import {
  trackAction,
  trackAddToCart,
  trackEvent,
  trackInitiateCheckout,
  trackPurchaseOnce,
  trackSafely,
} from '@/tracker';
import { formatForCurrency, getXAddress, sanitizeSourceMetadata } from '@/utils';

enableStaticRendering(typeof window === 'undefined');
const cartStorageKey = 'cartv3';
const favoritesStorageKey = 'favoritesv1';

const currenciesToLimit = ['USD', 'CAD', 'AUD', 'GBP', 'EUR'];

/** Matches the API limit of 25 gift cards per customer per 24 hours. */
export const CART_ITEM_LIMIT = 25;

/** English fallback when no React translator has been injected yet (tests, early adds). */
const fallbackCartMessage = (key, values = {}) => {
  let message = enMessages.cart[key] ?? key;
  for (const [name, value] of Object.entries(values)) {
    message = message.replaceAll(`{${name}}`, String(value));
  }
  return message;
};

// The charge statuses the checkout shows as a completed purchase (PaymentSuccess).
const paidStatuses = [statuses.success, statuses.overpaid, statuses.resolved];

export default class Cart {
  //TODO: Persist these 2 to localStorage
  items = [];
  favorites = [];
  referralCode = null;
  // end of persisted

  visible = false;
  creatingCharge = false;
  trackedPayment = false;
  lastOrder = null;
  loadingScript = false;
  supportBeaconLoaded = false;
  supportBeaconFailed = false;

  gclid = null;
  fbclid = null;
  rdt_cid = null;
  utmTags = {};

  dailyLimit = 9500;
  charge = null;
  order = null;
  quote = null;
  paymentSubmitted = false;
  showDappCheckout = false;
  isHydrated = false;
  // Loaded on demand at checkout — see loadPaymentCurrencies.
  paymentCurrencies = [];
  /** next-intl translator for the `cart` namespace, injected by StoreProvider. */
  _t = null;

  constructor() {
    this.storageReady = new Promise((resolve) => {
      this._resolveStorageReady = resolve;
    });

    makeObservable(this, {
      loadingScript: observable,
      supportBeaconLoaded: observable,
      supportBeaconFailed: observable,
      items: observable,
      favorites: observable,
      referralCode: observable,
      utmTags: observable,
      creatingCharge: observable,
      lastOrder: observable,
      visible: observable,
      add: action,
      remove: action,
      incrementQuantity: action,
      decrementQuantity: action,
      clearCart: action,
      toggleCart: action,
      trackedPayment: observable,
      cartCurrency: computed,
      cartCountry: computed,
      total: computed,
      totalItems: computed,
      totalInCents: computed,
      checkoutWithSingle: action,
      setReferralCode: action,
      setUTMTags: action,
      isHydrated: observable,
      hydrate: action,
      charge: observable,
      order: observable,
      quote: observable,
      qrCodeValue: computed,
      selectedCurrency: computed,
      unpaidAmount: computed,
      chainId: computed,
      paymentSubmitted: observable,
      getQuote: action,
      createOrder: action,
      setPaymentSubmitted: action,
      setCharge: action,
      setQuote: action,
      setOrder: action,
      accountNumber: computed,
      showDappCheckout: observable,
      setShowDappCheckout: action,
      setSupportBeaconLoaded: action,
      setSupportBeaconFailed: action,
      addToFavorites: action,
      removeFromFavorites: action,
      encodedItems: computed,
      populateCartFromEncodedItems: action,
      phantomUrl: computed,
      paymentCurrencies: observable.ref,
      setPaymentCurrencies: action,
      setTranslator: action,
    });
  }

  setTranslator = (t) => {
    this._t = t;
  };

  t = (key, values) => {
    if (this._t) return this._t(key, values);
    return fallbackCartMessage(key, values);
  };

  setPaymentCurrencies = (currencies) => {
    this.paymentCurrencies = currencies;
  };

  setSupportBeaconLoaded = (loaded = true) => {
    this.supportBeaconLoaded = loaded;
  };

  setSupportBeaconFailed = (failed = true) => {
    this.supportBeaconFailed = failed;
  };

  /**
   * currencies.json is ~110KB and only the checkout/payment UI needs it, so it is
   * kept out of the shared bundle and pulled in when checkout mounts. Consumers
   * read it through `selectedCurrency`, which recomputes once this resolves.
   */
  loadPaymentCurrencies = () => {
    if (!this._paymentCurrenciesPromise) {
      this._paymentCurrenciesPromise = import('@data/currencies.json')
        .then((module) => {
          this.setPaymentCurrencies(module.default ?? module);
        })
        .catch((error) => {
          this._paymentCurrenciesPromise = null;
          Bugsnag.notify(error);
          throw error;
        });
    }

    return this._paymentCurrenciesPromise;
  };

  /**
   * feathers + socket.io are only needed once a purchase starts, so they are
   * loaded on demand rather than shipped with every page. Returns a memoized
   * promise; callers that touch an API client must await it first.
   */
  initFeathersClients = () => {
    if (this.giftcardsAPIClient || this.paymentsAPIClient || this.blockchainAPIClient) {
      return Promise.resolve();
    }

    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    if (!this._feathersClientsPromise) {
      this._feathersClientsPromise = this._createFeathersClients().catch((error) => {
        this._feathersClientsPromise = null;
        Bugsnag.notify(error);
        throw error;
      });
    }

    return this._feathersClientsPromise;
  };

  _createFeathersClients = async () => {
    const { createClient, createRestClient } = await import('@/feathers-client');

    if (this.giftcardsAPIClient || this.paymentsAPIClient || this.blockchainAPIClient) {
      return;
    }

    this.giftcardsAPIClient = createClient(config.commerceApiUrl);
    this.paymentsAPIClient = createClient(config.paymentsApiUrl);
    this.blockchainAPIClient = createRestClient(config.blockchainApiUrl);

    this.giftcardsAPIClient.service('widget-orders').on('updated', ({ id, charge, order }) => {
      if (this.order && this.order.id === id) {
        if (typeof Bugsnag.leaveBreadcrumb === 'function')
          Bugsnag.leaveBreadcrumb('Order updated', { id, charge, order });
        this.setOrder(order);
        this.setCharge(charge);

        if (charge.statusCode >= statuses.processing) {
          const [paymentProtocol] = Object.keys(charge.payments);
          if (typeof Bugsnag.leaveBreadcrumb === 'function')
            Bugsnag.leaveBreadcrumb('Payment detected', { paymentProtocol });
          this.setPaymentSubmitted(false);

          void (async () => {
            await this.storageReady;
            const recentCurrencies = await this.storage.get('recentCurrencies');
            const list = Array.isArray(recentCurrencies) ? recentCurrencies : [];
            if (!list.includes(paymentProtocol)) {
              list.unshift(paymentProtocol);
              await this.storage.set('recentCurrencies', list);
            }
          })();
        }
      }
    });
  };

  async loadScriptAsync(uri) {
    return new Promise((resolve, reject) => {
      const tag = document.createElement('script');
      tag.src = uri;
      tag.id = 'commerce-sdk-script-tag';
      tag.async = true;
      tag.onload = () => {
        resolve();
      };
      tag.onerror = (_e) => {
        reject(new Error('Error loading Commerce SDK'));
      };
      const currentTag = document.getElementById(tag.id);
      if (currentTag) {
        currentTag.parentNode.removeChild(currentTag);
      }
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    });
  }

  async storeQueryParams() {
    const queryParams = new URLSearchParams(window.location.search);
    const r = queryParams.get('r');
    const fbclid = queryParams.get('fbclid');
    const gclid = queryParams.get('gclid');
    const gclsrc = queryParams.get('gclsrc');
    const rdt_cid = queryParams.get('rdt_cid');

    if (r) await this.setReferralCode(r);
    await Promise.all([
      this.setGoogleClickId(gclid, gclsrc),
      this.setFacebookClickId(fbclid),
      this.setRedditClickId(rdt_cid),
    ]);
    this.setUTMTags(queryParams);
  }

  async hydrate() {
    if (typeof window === 'undefined') return;

    if (this._hydratePromise) {
      await this._hydratePromise;
      await this.storeQueryParams();
      return;
    }

    this._hydratePromise = this._hydrateInternal();
    this.hydration = this._hydratePromise;
    await this._hydratePromise;
  }

  async _hydrateInternal() {
    const storage = await getPersistentStore();
    const cart = await storage.get(cartStorageKey);
    const favorites = await storage.get(favoritesStorageKey);

    runInAction(() => {
      this.storage = storage;
      this._resolveStorageReady?.();

      if (cart) {
        this.items = cart.items || [];

        if (cart.referralCode) {
          this.referralCode = cart.referralCode;
        }

        if (cart.utmTags) {
          this.utmTags = cart.utmTags;
        }
      }

      if (Array.isArray(favorites)) {
        this.favorites = favorites;
      }

      this.isHydrated = true;
    });

    autorun(() => {
      void this.storage.set(cartStorageKey, {
        items: this.items,
        referralCode: this.referralCode,
        utmTags: this.utmTags,
      });
    });

    autorun(() => {
      void this.storage.set(favoritesStorageKey, this.favorites);
    });

    await this.storeQueryParams();
  }

  async setReferralCode(referralCode) {
    this.referralCode = referralCode;
  }

  getExpiryRecord(value) {
    var expiryPeriod = 90 * 24 * 60 * 60 * 1000; // 90 day expiry in milliseconds

    var expiryDate = Date.now() + expiryPeriod;
    return {
      value: value,
      expiryDate: expiryDate,
    };
  }

  async setGoogleClickId(gclid, gclsrc) {
    await this.storageReady;
    let gclidRecord = null;
    const isGclsrcValid = !gclsrc || gclsrc.indexOf('aw') !== -1;

    if (gclid && isGclsrcValid) {
      gclidRecord = this.getExpiryRecord(gclid);
      await this.storage.set('gclid', gclidRecord);
    }

    var gclidObj = gclidRecord || (await this.storage.get('gclid'));
    var isGclidValid = gclidObj && Date.now() < gclidObj?.expiryDate;

    if (isGclidValid) {
      this.gclid = gclidObj.value;
    }
  }

  async setFacebookClickId(fbclid) {
    await this.storageReady;
    let fbclidRecord = null;
    if (fbclid) {
      fbclidRecord = this.getExpiryRecord(fbclid);
      await this.storage.set('fbclid', fbclidRecord);
    }

    var fbclidObj = fbclidRecord || (await this.storage.get('fbclid'));
    var isFbclidValid = fbclidObj && Date.now() < fbclidObj?.expiryDate;

    if (isFbclidValid) {
      this.fbclid = fbclidObj.value;
    }
  }

  async setRedditClickId(rdt_cid) {
    await this.storageReady;
    let rdt_cidRecord = null;
    if (rdt_cid) {
      rdt_cidRecord = this.getExpiryRecord(rdt_cid);
      await this.storage.set('rdt_cid', rdt_cidRecord);
    }

    var rdt_cidObj = rdt_cidRecord || (await this.storage.get('rdt_cid'));
    var isRdt_cidValid = rdt_cidObj && Date.now() < rdt_cidObj?.expiryDate;

    if (isRdt_cidValid) {
      this.rdt_cid = rdt_cidObj.value;
    }
  }

  async setUTMTags(params) {
    params.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        this.utmTags[key] = value;
      }
    });
  }

  get totalItems() {
    return this.items.map((item) => item.quantity).reduce((a, b) => a + b, 0);
  }

  //TODO: Handle strings in here
  get total() {
    if (!this.totalItems) {
      return 0;
    }
    return this.items.map((item) => item.quantity * item.amount).reduce((a, b) => a + b, 0) / 100;
  }

  //TODO: Handle strings in here
  get totalInCents() {
    return this.total * 100;
  }

  get cartCurrency() {
    return this.items.length ? this.items[0].product.currency : null;
  }

  get cartCountry() {
    return this.items.length ? this.items[0].brand.country : null;
  }

  get encodedItems() {
    const cleanedUpItems = this.items.map((item) => ({
      brand: item.brand.id,
      product: item.product.id,
      quantity: item.quantity,
      amount: item.amount,
      isTopUp: item.isTopUp,
      isESim: item.isESim,
      ...(item.accountNumber ? { accountNumber: item.accountNumber } : {}),
    }));

    return btoa(JSON.stringify(cleanedUpItems));
  }

  get phantomUrl() {
    if (typeof window === 'undefined') return null;
    return `${window.location.origin}/checkout?items=${this.encodedItems}&chargeId=${this.charge?.id}&quoteId=${this.quote?.id}&utm_source=checkout_phantom`;
  }

  populateCartFromEncodedItems = async (encodedItems) => {
    await this.initFeathersClients();

    const items = JSON.parse(atob(encodedItems));
    const populatedItems = await Promise.all(
      items.map(async (item) => {
        const brand = await this.giftcardsAPIClient.service('brands').get(item.brand);
        const product = await this.giftcardsAPIClient.service('products').get(item.product);

        return {
          ...item,
          brand,
          product,
        };
      })
    );

    this.items = populatedItems;
  };

  toggleCart = () => {
    this.visible = !this.visible;
  };

  initDappCheckout = () => {
    console.log('initDappCheckout');
    this.setShowDappCheckout(true);
  };

  add = (
    quantity,
    amount,
    brand,
    product,
    isTopUp = false,
    accountNumber,
    { track = true } = {}
  ) => {
    const isESim = brand.type === 'esim';
    if (
      this.items.some((item) => item.isTopUp || item.isESim) ||
      ((isTopUp || isESim) && this.items.length)
    ) {
      const message = this.items.some((item) => item.brand.id === brand.id)
        ? this.t('oneTopUpOrEsim')
        : this.t('purchaseSeparately');

      toast.error(message);
      return false;
    }

    // TODO: How does this handle quoteCurrency being different then the currency
    if (this.cartCurrency && this.cartCurrency !== product.currency) {
      toast.error(this.t('sameCurrency'));
      return false;
    }

    if (this.items.length && this.items[0].product.quoteCurrency !== product.quoteCurrency) {
      toast.error(this.t('purchaseSeparately'));
      return false;
    }

    //TODO: Handle strings in here
    if (
      (quantity * amount) / 100 + this.total >= this.dailyLimit &&
      currenciesToLimit.includes(product.cartCurrency)
    ) {
      toast.error(this.t('dailyLimit', { limit: this.dailyLimit }));
      return false;
    }

    if (this.totalItems + quantity > CART_ITEM_LIMIT) {
      toast.error(this.t('itemLimitExceeded', { limit: CART_ITEM_LIMIT }));
      return false;
    }

    let itemExisted = false;
    this.items.forEach((item) => {
      //TODO: Handle strings in here for amount
      // Update the quantity if the item exists
      if (item.brand.id === brand.id && item.amount === amount) {
        itemExisted = true;
        item.quantity = item.quantity + quantity;
        return;
      }
    });
    if (!itemExisted) {
      this.items.push({ quantity, amount, brand, product, isTopUp, isESim, accountNumber });
    }
    const formattedAmount = formatForCurrency({ amount, currency: product.currency });
    const message =
      quantity > 1
        ? this.t('addedMany', { quantity, amount: formattedAmount, brand: brand.name })
        : this.t('added', { amount: formattedAmount, brand: brand.name });

    this.setCharge(null);
    this.setQuote(null);
    this.setOrder(null);
    this.setPaymentSubmitted(false);

    toast.success(message);
    // GA4 add_to_cart. incrementQuantity opts out: it sends its own.
    if (track) trackSafely(trackAddToCart, brand, amount, quantity, product);
    return true;
  };

  //TODO: Handle strings in here
  remove = (itemToRemove) => {
    const isItem = (i) => i.brand.id === itemToRemove.brand.id && i.amount === itemToRemove.amount;
    const filtered = this.items.filter((item) => !isItem(item));

    this.items = filtered;
    this.setCharge(null);
    this.setQuote(null);
    this.setOrder(null);
    this.setPaymentSubmitted(false);

    trackAction('remove', {
      id: itemToRemove.brand.id,
      name: `${itemToRemove.brand.name} gift card`,
      category: itemToRemove.brand.country.name,
      brand: itemToRemove.brand.name,
      price: numeral(itemToRemove.amount).divide(100).format('0.00'),
      quantity: itemToRemove.quantity,
    });
    trackEvent({
      category: 'User',
      action: 'Removed from cart',
      label: itemToRemove.brand.name,
      value: itemToRemove.quantity,
    });
  };

  addToFavorites = (brand) => {
    if (!this.favorites.some((favorite) => favorite.id === brand.id)) {
      this.favorites.push(brand);
      toast.success(this.t('addedFavorite', { brand: brand.name }));
    }
  };

  removeFromFavorites = (brand) => {
    const wasFavorite = this.favorites.some((favorite) => favorite.id === brand.id);
    this.favorites = this.favorites.filter((favorite) => favorite.id !== brand.id);
    if (wasFavorite) {
      toast.success(this.t('removedFavorite', { brand: brand.name }));
    }
  };

  //TODO: Handle strings in here for amount
  decrementQuantity = (itemToDecrement) => {
    if (itemToDecrement.quantity === 1) {
      this.remove(itemToDecrement);
    } else {
      this.items.forEach((item) => {
        // Update the quantity if the item exists
        if (item.brand.id === itemToDecrement.brand.id && item.amount === itemToDecrement.amount) {
          item.quantity = item.quantity - 1;
        }
      });

      this.setCharge(null);
      this.setQuote(null);
      this.setOrder(null);
      this.setPaymentSubmitted(false);
    }

    trackAction('remove', {
      id: itemToDecrement.brand.id,
      name: `${itemToDecrement.brand.name} gift card`,
      category: itemToDecrement.brand.country.name,
      brand: itemToDecrement.brand.name,
      price: numeral(itemToDecrement.amount).divide(100).format('0.00'),
      quantity: 1,
    });

    trackEvent({
      category: 'User',
      action: 'Decremented Quantity in Cart',
      label: itemToDecrement.brand.name,
      value: 1,
    });
  };

  incrementQuantity = (item) => {
    const added = this.add(1, item.amount, item.brand, item.product, false, undefined, {
      track: false,
    });
    if (!added) return;

    trackAction('add', {
      id: item.brand.id,
      name: `${item.brand.name} gift card`,
      category: item.brand.country.name, // Using region for a category
      brand: item.brand.name,
      price: numeral(item.amount).divide(100).format('0.00'),
      quantity: 1,
    });

    trackEvent({
      category: 'User',
      action: 'Incremented Quantity in Cart',
      label: item.brand.name,
      value: 1,
    });
  };

  clearCart = () => {
    this.items = [];
    this.setCharge(null);
    this.setQuote(null);
    this.setOrder(null);
    this.setPaymentSubmitted(false);
  };

  checkoutWithSingle = async (brand, product, amount, accountNumber) => {
    const added = this.add(1, amount, brand, product, true, accountNumber);
    // GA4 begin_checkout: "Purchase" on a brand page goes straight to checkout.
    if (added) trackSafely(trackInitiateCheckout, this, 'Purchase now');
    return added;
  };

  createOrderPayload = (customer, platform) => {
    let apiKey;
    const { pathname, search } = window.location;
    const urlParams = new URLSearchParams(search);
    const queryStringKey = (urlParams.get('key') || '').trim();

    if (queryStringKey.length > 0) {
      apiKey = queryStringKey;
    } else if (window.bidaliProvider) {
      apiKey = window.bidaliProvider.key;
    } else if (window.valora) {
      apiKey = window.valora.key;
    } else if ((!window.bidaliProvider && !window.valora) || pathname === '/checkout') {
      apiKey = config.commerceReferrerApiKey;
    }

    const { proof, ...customerFields } = customer || {};
    const payload = {
      referralCode: this.referralCode || undefined,
      apiKey,
      customer: customerFields,
      items: this.items.map((item) => ({
        amount: item.amount,
        quantity: item.quantity,
        productId: item.product.id,
      })),
      source: this.utmTags,
    };

    if (proof) {
      payload.proof = proof;
    }

    if (platform) {
      payload.source = {
        ...this.utmTags,
        ...platform,
      };
    }

    if (this.gclid) {
      payload.source.gclid = this.gclid;
    }

    if (this.fbclid) {
      payload.source.fbclid = this.fbclid;
    }

    if (this.rdt_cid) {
      payload.source.rdt_cid = this.rdt_cid;
    }

    const isEsimOrder = this.items.some((item) => item.brand.type === 'esim');
    //TODO: Remove this once we have a way to handle esim orders with IMEI being passed
    // as accountNumber
    if (this.accountNumber && !isEsimOrder) {
      payload.accountNumber = this.accountNumber;
    }

    const sanitizedSource = sanitizeSourceMetadata(payload.source);

    payload.source = sanitizedSource;

    return payload;
  };

  createOrder = async (customer, platform = null) => {
    if (!this.items.length) {
      const error = new Error('Cannot create an order with an empty cart');
      error.data = { message: this.t('emptyCheckout') };
      Bugsnag.notify(error);
      throw error;
    }

    await this.initFeathersClients();

    if (!this.paymentsAPIClient || !this.giftcardsAPIClient) {
      const error = new Error('Store not properly initialized');
      Bugsnag.notify(error);
      throw error;
    }

    try {
      const payload = this.createOrderPayload(customer, platform);
      const query = { organization: config.paymentsApiPublishableKey };
      if (typeof Bugsnag.leaveBreadcrumb === 'function') {
        const { proof, ...breadcrumbPayload } = payload;
        Bugsnag.leaveBreadcrumb('Creating Order', {
          payload: {
            ...breadcrumbPayload,
            proof: proof ? { type: proof.type } : undefined,
          },
        });
      }
      const { charge, order } = await this.giftcardsAPIClient
        .service('widget-orders')
        .create(payload);
      this.setCharge(await this.paymentsAPIClient.service('charges').get(charge.id, { query }));
      this.setOrder(order);
    } catch (e) {
      Bugsnag.notify(e);
      throw e;
    }
  };

  getQuote = async (id) => {
    try {
      await this.initFeathersClients();
      const query = { organization: config.paymentsApiPublishableKey };
      const quote = await this.paymentsAPIClient.service('quotes').get(id, { query });
      this.setQuote(quote);
      return quote;
    } catch (e) {
      Bugsnag.notify(e);
      throw e;
    }
  };

  getCharge = async (id) => {
    await this.initFeathersClients();
    const query = { organization: config.paymentsApiPublishableKey };
    const charge = await this.paymentsAPIClient.service('charges').get(id, { query });
    this.setCharge(charge);
    return charge;
  };

  setPaymentSubmitted = (value) => {
    this.paymentSubmitted = value;
  };

  setCharge = (charge) => {
    this.charge = charge;
    if (charge?.quotes) {
      const quoteSummary = Object.entries(charge.quotes).map(([protocol, quote]) => ({
        protocol,
        amount: quote?.amount,
        status: quote?.status,
        currency: quote?.currency,
      }));
      const zeroAmount = quoteSummary.filter(
        (q) => !q.amount || Number(q.amount) === 0 || Number.isNaN(Number(q.amount))
      );
      console.log('[cart] charge quotes', {
        chargeId: charge.id,
        quoteCount: quoteSummary.length,
        zeroAmountProtocols: zeroAmount.map((q) => q.protocol),
        quotes: quoteSummary,
      });
      if (zeroAmount.length > 0) {
        console.warn(
          '[cart] charge includes zero/missing quote amounts (often missing rates)',
          zeroAmount
        );
      }
    }
    // GA4 purchase, once per charge however often a paid charge arrives here.
    if (charge && paidStatuses.includes(charge.statusCode)) {
      trackSafely(trackPurchaseOnce, this, charge, 'Checkout');
    }
  };

  setQuote = (quote) => {
    if (quote) {
      console.log('[cart] setQuote', {
        id: quote.id,
        amount: quote.amount,
        currency: quote.currency,
        status: quote.status,
      });
    }
    this.quote = quote;
  };

  setOrder = (order) => {
    this.order = order;
  };

  setShowDappCheckout = (value) => {
    this.showDappCheckout = value;
  };

  get selectedCurrency() {
    if (this.quote) {
      return this.paymentCurrencies.find((currency) => currency.symbol === this.quote.currency);
    }

    return null;
  }

  get unpaidAmount() {
    if (!this.charge) {
      return null;
    }

    const payment =
      Object.keys(this.charge?.payments).length &&
      this.charge.payments[this.selectedCurrency?.protocol];

    return payment?.outstanding || this.quote?.amount;
  }

  get qrCodeValue() {
    const { address } = this.quote || {};
    const sendAddress = getXAddress(this.selectedCurrency, this.quote) || address;

    if (this.selectedCurrency?.addressOnlyInQR) {
      return sendAddress;
    }

    if (this.selectedCurrency?.servicePath === 'solana') {
      return this.web3Utils.getSolanaPayQR({
        to: sendAddress,
        amount: this.unpaidAmount,
        memo: this.quote.extraId,
        description: this.charge.description,
        splTokenAddress: this.selectedCurrency?.contractAddress,
      });
    }

    let params = { amount: this.unpaidAmount };
    let baseUrl = `${this.selectedCurrency?.protocol}:${sendAddress}`;

    if (this.selectedCurrency?.protocol === 'celo' || this.selectedCurrency?.isCeloERC20) {
      baseUrl = 'celo://wallet/pay';
      params = {
        address: sendAddress,
        token: this.selectedCurrency?.displaySymbol,
        displayName: 'Bidali',
      };

      if (this.selectedCurrency.fiat) {
        params.currencyCode = this.selectedCurrency?.fiat;
      }
    }

    if (this.selectedCurrency?.servicePath === 'algorand') {
      baseUrl = `algorand://${sendAddress}`;
      params = {};

      if (this.selectedCurrency?.algorandAssetId) {
        return this.quote.address;
      }

      if (this.quote?.extraId) {
        params.xnote = this.quote?.extraId;
      }
    }

    if (this.selectedCurrency?.protocol === 'ethereum') {
      params.value = this.unpaidAmount;
    }

    const qs = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&');

    return `${baseUrl}?${qs}`;
  }

  get chainId() {
    const env = config.commerceEnv === 'production' ? 'mainnet' : 'testnet';

    if (!this.selectedCurrency?.network?.chainIds) {
      return null;
    }

    return this.selectedCurrency.network.chainIds[env];
  }

  get accountNumber() {
    const { accountNumber } = this.items.find((item) => item.accountNumber) || {};
    return accountNumber || null;
  }
}
